import React from "react";
import { motion } from "framer-motion";
import { Smartphone, LayoutDashboard, Eye, Hammer, Database, BarChart3, Building2, Bot } from "lucide-react";
import Layout from "../components/Layout";
import SEO from "../components/SEO";
import SectionHeading from "../components/SectionHeading";
import { IMAGES } from "../lib/images";

const MODULES = [
  { i: LayoutDashboard, n: "01", t: "Investor Dashboard", b: "Real-time capital, milestone, and reporting dashboard for institutional partners." },
  { i: Eye, n: "02", t: "Virtual Tower Tours", b: "Immersive WebGL walkthroughs of branded residences, amenities, and observation experiences." },
  { i: Hammer, n: "03", t: "Construction Updates", b: "Live construction tracking with drone footage, weekly executive briefings, and milestone alerts." },
  { i: Database, n: "04", t: "CRM Integration", b: "Unified residence sales, leasing, hospitality, and investor pipelines in a single CRM surface." },
  { i: Smartphone, n: "05", t: "Smart Building Resident App", b: "Concierge, access, dining, valet, wellness, and residence control — in one ultra-private app." },
  { i: Bot, n: "06", t: "AI Development Analytics", b: "AI-driven absorption, pricing, and demand analytics across every active development." },
  { i: BarChart3, n: "07", t: "Investor Reporting", b: "Quarterly performance, distributions, and audited statements delivered in-app." },
  { i: Building2, n: "08", t: "Operator Console", b: "Operations console for property management, hospitality, and asset management teams." },
];

export default function AppEcosystemPage() {
  return (
    <Layout>
      <SEO title="Future App Ecosystem" description="The EID digital platform — investor dashboards, virtual tours, smart-building resident apps, AI-driven analytics." path="/app-ecosystem" />
      <section className="relative pt-40 pb-16 px-6 lg:px-12 overflow-hidden">
        <div className="absolute -right-32 top-32 w-[60%] h-[600px] opacity-30">
          <img src={IMAGES.observation_deck_view} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-l from-transparent to-background" />
        </div>
        <div className="relative max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="overline">— Future ecosystem</div>
            <div className="overline mt-2 text-foreground/50">EID · Digital · 2026 — 2030</div>
          </div>
          <div className="lg:col-span-8">
            <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }} className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.98] font-light tracking-tight max-w-5xl">
              A digital ecosystem for <em className="text-primary italic">institutional</em> real estate.
            </motion.h1>
            <p className="mt-8 text-muted-foreground text-lg max-w-2xl leading-relaxed">
              EID is building a vertically integrated technology stack —
              spanning investor portals, virtual tours, resident apps, and
              AI-driven analytics — to power the next generation of luxury
              vertical urbanism.
            </p>
          </div>
        </div>
      </section>

      <section className="py-32 lg:py-44 px-6 lg:px-12">
        <div className="max-w-[1600px] mx-auto">
          <SectionHeading overline="— Modules" title="Eight pillars of the EID digital platform." />
          <div className="mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {MODULES.map((m, i) => {
              const Icon = m.i;
              return (
                <motion.div
                  key={m.n}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] }}
                  data-testid={`module-${m.n}`}
                  className="glass p-8 group hover:border-primary/40 transition-colors"
                >
                  <Icon className="w-7 h-7 text-primary mb-6" />
                  <div className="font-mono text-primary text-sm tracking-widest">{m.n}</div>
                  <h3 className="mt-3 font-serif text-2xl tracking-tight">{m.t}</h3>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{m.b}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-32 px-6 lg:px-12 bg-card border-y border-primary/10">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="overline mb-6">— Vision</div>
            <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] font-light tracking-tight">
              The most integrated developer platform in luxury real estate.
            </h2>
            <p className="mt-6 text-muted-foreground text-lg leading-relaxed max-w-xl">
              From the first capital commitment to the final keys handed to a
              penthouse resident — every touchpoint runs through one EID
              digital surface.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div className="aspect-[3/4] overflow-hidden"><img src={IMAGES.luxury_condo_interior} alt="Residence" className="w-full h-full object-cover" /></div>
            <div className="aspect-[3/4] overflow-hidden mt-12"><img src={IMAGES.luxury_amenity_spa} alt="Amenity" className="w-full h-full object-cover" /></div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
